import { useMemo } from 'react';

import {
  useGetDepartments,
  useGetTags,
  useGetUserGroups,
  useGetUsers,
} from '@/hooks/queries';
import type { JSONObject } from '@/types/types';

import type { Dataset, FieldConfig, TableFields, TableRecord } from '../types';
import type { CustomAttributeAlias } from '../utils/coalesceCustomAttributeFields';
import {
  applyCustomAttributeAliases,
  NO_ALIASES,
} from '../utils/coalesceCustomAttributeFields';
import { getCustomAttributeDataForRecord } from '../utils/customAttributes';

export type TableFieldsWithCustomAttributes<T extends TableRecord> =
  TableFields<T> & {
    [key: string]: FieldConfig<{
      CustomAttributeData: JSONObject;
    }>;
  };

type Options<T extends TableRecord> = {
  data?: Dataset<T> | undefined;
  tableFields: TableFieldsWithCustomAttributes<T>;
  /** Each row's value is projected onto the group's primary path. */
  aliases?: CustomAttributeAlias[];
};

const isJsonObject = (value: unknown): value is JSONObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Rewrites a record's `CustomAttributeData` onto primary paths, returning the
 * original record reference when nothing needed projecting so row identity stays
 * stable.
 */
const projectRecord = <T extends TableRecord>(
  record: T,
  aliases: CustomAttributeAlias[]
): T => {
  const data = record.CustomAttributeData;
  if (!isJsonObject(data)) {
    return record;
  }

  const projected = applyCustomAttributeAliases(data, aliases);

  return projected === data
    ? record
    : { ...record, CustomAttributeData: projected };
};

const buildLookup = <T>(
  rows: T[],
  getKey: (row: T) => string | null | undefined,
  getValue: (row: T) => string | null | undefined
): Record<string, string> => {
  const lookup: Record<string, string> = {};
  for (const row of rows) {
    const key = getKey(row);
    const value = getValue(row);
    if (key && value) {
      lookup[key] = value;
    }
  }

  return lookup;
};

/**
 * Spreads custom attribute fields onto the dataset so they can be used as
 * columns in the datagrid. Lookups (users, user groups, department types,
 * tag types) stay `undefined` until every required query has settled, so
 * cell values don't flicker as the underlying queries land at different
 * times.
 */
export const useAddCustomAttributeFieldData = <T extends TableRecord>({
  data,
  tableFields,
  aliases = NO_ALIASES,
}: Options<T>) => {
  const isGroupLookupRequired = useMemo(
    () =>
      Object.values(tableFields).some(
        (field) => field.custom && field.isGroupLookupRequired
      ),
    [tableFields]
  );

  const isTagLookupRequired = useMemo(
    () =>
      Object.values(tableFields).some(
        (field) => field.custom && field.isTagLookupRequired
      ),
    [tableFields]
  );

  const { data: userData, loading: userLoading } = useGetUsers({
    queryArgs: {},
  });
  const { data: userGroupData, loading: userGroupLoading } = useGetUserGroups({
    queryArgs: {},
    shouldSkip: !isGroupLookupRequired,
  });
  const { data: departmentTypeData } = useGetDepartments({ queryArgs: {} });
  const { data: tagTypeData } = useGetTags({
    queryArgs: {},
    shouldSkip: !isTagLookupRequired,
  });

  // userLookup and groupLookup feed the same field types (custom user-multi
  // selects), so they must surface together to avoid the cell flickering
  // through "user only" → "user + group". departmentTypeLookup feeds a
  // different field type and is independent.
  const userOrGroupLookupsLoading =
    userLoading || (isGroupLookupRequired && userGroupLoading);

  const userLookup = useMemo(() => {
    if (userOrGroupLookupsLoading || !userData) {
      return undefined;
    }

    return buildLookup(
      userData.user,
      (u) => u.Id,
      (u) => u.FriendlyName
    );
  }, [userData, userOrGroupLookupsLoading]);

  const groupLookup = useMemo(() => {
    if (userOrGroupLookupsLoading || !isGroupLookupRequired || !userGroupData) {
      return undefined;
    }

    return buildLookup(
      userGroupData.user_group,
      (g) => g.Id,
      (g) => g.Name
    );
  }, [userGroupData, isGroupLookupRequired, userOrGroupLookupsLoading]);

  const departmentTypeLookup = useMemo(() => {
    if (!departmentTypeData) {
      return undefined;
    }

    return buildLookup(
      departmentTypeData.department_type,
      (d) => d.DepartmentTypeId,
      (d) => d.Name
    );
  }, [departmentTypeData]);

  const tagTypeLookup = useMemo(() => {
    if (!isTagLookupRequired || !tagTypeData) {
      return undefined;
    }

    return buildLookup(
      tagTypeData.tag_type,
      (t) => t.TagTypeId,
      (t) => t.Name
    );
  }, [tagTypeData, isTagLookupRequired]);

  // Get all fields and convert into table columns.
  return useMemo(
    () =>
      (data ?? []).map((record) => {
        // Project before flattening: cell renderers read either the flattened
        // value below or `CustomAttributeData` off the record itself, and both
        // derive from `projected`.
        const projected = projectRecord(record, aliases);

        return {
          ...projected,
          ...getCustomAttributeDataForRecord(tableFields, projected, {
            userLookup,
            groupLookup,
            departmentTypeLookup,
            tagTypeLookup,
          }),
        };
      }),
    [
      tableFields,
      data,
      aliases,
      departmentTypeLookup,
      userLookup,
      groupLookup,
      tagTypeLookup,
    ]
  );
};
